// GA4 ecommerce tracking utilities for GrowOut Co.
// Tracks lead generation (free trials) and ecommerce sales (paid plans)
// with free-to-paid upgrade attribution.
import { priceNumber } from '../lib/pricing';

// Prices come from the canonical pricing source so GA4 revenue cannot drift
// from the displayed prices. free_preview maps to the canonical "free" plan.
const PLAN_DATA = {
  free_preview: { id: 'free_preview', name: 'Free Preview', category: 'Free Trial', price: priceNumber('free') },
  trial9: { id: 'trial9', name: 'Trial $9', category: 'Paid', price: priceNumber('trial9') },
  launch_static: { id: 'launch_static', name: 'Launch Static', category: 'Paid', price: priceNumber('launch_static') },
  scale_static: { id: 'scale_static', name: 'Scale Static', category: 'Paid', price: priceNumber('scale_static') },
  dominate_static: { id: 'dominate_static', name: 'Dominate Static', category: 'Paid', price: priceNumber('dominate_static') },
  launch_video: { id: 'launch_video', name: 'Launch Video', category: 'Paid', price: priceNumber('launch_video') },
  scale_video: { id: 'scale_video', name: 'Scale Video', category: 'Paid', price: priceNumber('scale_video') },
  dominate_video: { id: 'dominate_video', name: 'Dominate Video', category: 'Paid', price: priceNumber('dominate_video') },
  launch_bundle: { id: 'launch_bundle', name: 'Launch Bundle', category: 'Paid', price: priceNumber('launch_bundle') },
  scale_bundle: { id: 'scale_bundle', name: 'Scale Bundle', category: 'Paid', price: priceNumber('scale_bundle') },
  dominate_bundle: { id: 'dominate_bundle', name: 'Dominate Bundle', category: 'Paid', price: priceNumber('dominate_bundle') },
};

const LEAD_DATE_KEY = 'growout_lead_date';
const LEAD_SOURCE_KEY = 'growout_lead_source';

const safeGtag = (event, params) => {
  if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
    try {
      window.gtag('event', event, params);
    } catch (err) {
      console.warn('GA4 tracking error:', err);
    }
  }
};

const START_PLAN_MAP = {
  free: 'free',
  trial9: 'trial9',
  launch_static: 'launch_static',
  scale_static: 'scale_static',
  dominate_static: 'dominate_static',
  launch_video: 'launch_video',
  scale_video: 'scale_video',
  dominate_video: 'dominate_video',
  launch_bundle: 'launch_bundle',
  scale_bundle: 'scale_bundle',
  dominate_bundle: 'dominate_bundle',
  // Retired slug aliases resolve to current canonical static SKUs
  growth: 'launch_static',
  performance: 'scale_static',
  engine: 'dominate_static',
};

const START_TO_INTERNAL_PLAN_MAP = {
  free: 'free_preview',
  trial9: 'trial9',
  launch_static: 'launch_static',
  scale_static: 'scale_static',
  dominate_static: 'dominate_static',
  launch_video: 'launch_video',
  scale_video: 'scale_video',
  dominate_video: 'dominate_video',
  launch_bundle: 'launch_bundle',
  scale_bundle: 'scale_bundle',
  dominate_bundle: 'dominate_bundle',
};

export const mapUrlToStartPlan = (urlPlan) => START_PLAN_MAP[urlPlan] || urlPlan;

// Map the URL plan slug (e.g. "free", "growth", "launch_static") to our internal plan key.
export const mapUrlToPlanKey = (urlPlan) => {
  const startPlan = mapUrlToStartPlan(urlPlan);
  return START_TO_INTERNAL_PLAN_MAP[startPlan] || urlPlan;
};

// Track when the pricing section becomes visible.
export const trackViewPricingList = () => {
  const items = Object.values(PLAN_DATA).map((plan) => ({
    item_id: plan.id,
    item_name: plan.name,
    item_category: plan.category,
    price: plan.price,
    quantity: 1,
  }));

  safeGtag('view_item_list', {
    item_list_id: 'pricing_section',
    item_list_name: 'GrowOut Plans',
    items,
  });
};

// Track when the user clicks a pricing card.
export const trackSelectPlan = (planKey) => {
  const plan = PLAN_DATA[planKey];
  if (!plan) return;

  safeGtag('select_item', {
    item_list_id: 'pricing_section',
    item_list_name: 'GrowOut Plans',
    items: [{
      item_id: plan.id,
      item_name: plan.name,
      item_category: plan.category,
      price: plan.price,
      quantity: 1,
    }],
  });
};

// Track when checkout starts (Stripe OR free brief).
export const trackBeginCheckout = (planKey) => {
  const plan = PLAN_DATA[planKey];
  if (!plan) return;

  safeGtag('begin_checkout', {
    currency: 'USD',
    value: plan.price,
    plan: plan.id,
    is_free: plan.category === 'Free Trial',
    items: [{
      item_id: plan.id,
      item_name: plan.name,
      item_category: plan.category,
      price: plan.price,
      quantity: 1,
    }],
  });
};

// Track Stripe payment success landing (paid only).
export const trackPaymentInfo = (planKey) => {
  const plan = PLAN_DATA[planKey];
  if (!plan || plan.category === 'Free Trial') return;

  safeGtag('add_payment_info', {
    currency: 'USD',
    value: plan.price,
    payment_type: 'card',
    plan: plan.id,
    items: [{
      item_id: plan.id,
      item_name: plan.name,
      item_category: plan.category,
      price: plan.price,
      quantity: 1,
    }],
  });
};

// Track brief form first interaction.
export const trackFormStart = (planKey) => {
  const plan = PLAN_DATA[planKey];
  if (!plan) return;

  safeGtag('form_start', {
    form_id: 'brief_form',
    form_name: 'GrowOut Brief',
    form_destination: typeof window !== 'undefined' ? window.location.href : '',
    plan: plan.id,
    is_free: plan.category === 'Free Trial',
  });
};

// Track brief form submit.
export const trackFormSubmit = (planKey) => {
  const plan = PLAN_DATA[planKey];
  if (!plan) return;

  safeGtag('form_submit', {
    form_id: 'brief_form',
    form_name: 'GrowOut Brief',
    plan: plan.id,
    is_free: plan.category === 'Free Trial',
  });
};

// Track FREE brief completion (lead generation).
export const trackGenerateLead = () => {
  // Save lead timestamp for future free-to-paid attribution.
  const leadDate = new Date().toISOString();
  try {
    localStorage.setItem(LEAD_DATE_KEY, leadDate);
    localStorage.setItem(LEAD_SOURCE_KEY, 'free_preview_brief');
  } catch (e) {
    // localStorage may be blocked
  }

  safeGtag('generate_lead', {
    currency: 'USD',
    value: 0,
    lead_source: 'free_preview_brief',
    plan: 'free_preview',
    user_type: 'free_lead',
  });

  if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
    window.gtag('set', 'user_properties', { user_type: 'free_lead' });
  }
};

// Track PAID brief completion (purchase + check for free-to-paid upgrade).
export const trackPurchase = (planKey, sessionId) => {
  const plan = PLAN_DATA[planKey];
  if (!plan || plan.category === 'Free Trial') return;

  let originalLeadDate = null;
  let leadSource = 'direct_paid';
  let userType = 'direct_paid';
  let daysToConversion = 0;

  try {
    originalLeadDate = localStorage.getItem(LEAD_DATE_KEY);
    if (originalLeadDate) {
      leadSource = 'free_preview_upgrade';
      userType = 'free_to_paid';
      const leadTime = new Date(originalLeadDate).getTime();
      const nowTime = new Date().getTime();
      daysToConversion = Math.round((nowTime - leadTime) / (1000 * 60 * 60 * 24));
    }
  } catch (e) {
    // localStorage may be blocked
  }

  const transactionId = sessionId || `txn_${Date.now()}`;

  // Purchase event - counts in GA4 revenue regardless of source.
  safeGtag('purchase', {
    transaction_id: transactionId,
    value: plan.price,
    currency: 'USD',
    plan: plan.id,
    lead_source: leadSource,
    user_type: userType,
    original_lead_date: originalLeadDate || '',
    days_to_conversion: daysToConversion,
    items: [{
      item_id: plan.id,
      item_name: plan.name,
      item_category: plan.category,
      price: plan.price,
      quantity: 1,
    }],
  });

  if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
    window.gtag('set', 'user_properties', { user_type: userType });
  }

  // Additional upgrade_to_paid event for free-to-paid attribution.
  if (leadSource === 'free_preview_upgrade') {
    safeGtag('upgrade_to_paid', {
      transaction_id: transactionId,
      value: plan.price,
      currency: 'USD',
      plan: plan.id,
      original_lead_date: originalLeadDate,
      days_to_conversion: daysToConversion,
      lead_source: 'free_preview_upgrade',
    });

    try {
      localStorage.removeItem(LEAD_DATE_KEY);
      localStorage.removeItem(LEAD_SOURCE_KEY);
    } catch (e) {
      // ignore
    }
  }
};

// ---------------------------------------------------------------------------
// Meta Pixel (browser) + CAPI dedup helpers.
//
// These live ALONGSIDE the GA4/PostHog tracking above and never replace it.
// Every Meta call is fail-safe: a missing pixel, a blocked cookie, or a network
// error must never throw into a page render or the checkout redirect. During the
// build-time prerender crawl (window.__PRERENDER__ === true) every Meta call is a
// no-op, matching the guarded base snippet in public/index.html.
//
// Dedup model: each Meta browser event carries an eventID, and a server-side CAPI
// copy fires the same event_name with the SAME eventID so Meta dedups the pair.
//   - Purchase / StartTrial use eventID = client_reference_id, which is known to
//     BOTH the browser and the Stripe webhook, so nothing extra has to be threaded
//     through the Payment Link.
//   - InitiateCheckout uses a fresh uuid that we also stash server-side so a CAPI
//     copy can reuse it.
// ---------------------------------------------------------------------------

// Backend CAPI endpoints (TIER 1.7). The stash stores the per-checkout
// identifiers the Stripe webhook cannot see (_fbp, _fbc, the browser eventID, the
// source URL), keyed by client_reference_id. The track endpoint mirrors a browser
// event server-side so the browser + server pair dedups on the shared event_id.
const CAPI_STASH_PATH = '/api/meta/checkout-context';
const CAPI_TRACK_PATH = '/api/meta/track';

// localStorage that threads checkout context to the on-domain success route,
// where the browser Purchase / StartTrial fires. Keyed PER checkout by
// client_reference_id (not one global key) so the success route can positively
// match the resolved session to its OWN stored context - a stale or mismatched
// entry must never fire a phantom conversion. Entries carry a created_at and are
// swept after the TTL (and consumed on a successful fire).
const CHECKOUT_CONTEXT_PREFIX = 'growout_capi_checkout_';
const CHECKOUT_CONTEXT_TTL_MS = 24 * 60 * 60 * 1000; // 24h, matches the server stash TTL

const isPrerender = () =>
  typeof window !== 'undefined' && window.__PRERENDER__ === true;

const generateEventId = () => {
  try {
    if (typeof window !== 'undefined' && window.crypto && typeof window.crypto.randomUUID === 'function') {
      return window.crypto.randomUUID();
    }
  } catch (e) {
    // fall through to manual generator
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

// The one helper: fire a Meta Pixel event with an eventID and return that eventID
// so a server copy can reuse it for dedup. Never throws, no-ops during prerender
// or when fbq is unavailable.
const safeMetaTrack = (eventName, customData = {}, providedEventId = null) => {
  const eventId = providedEventId || generateEventId();
  try {
    if (isPrerender()) return eventId;
    if (typeof window !== 'undefined' && typeof window.fbq === 'function') {
      window.fbq('track', eventName, customData, { eventID: eventId });
    }
  } catch (err) {
    console.warn('Meta Pixel tracking error:', err);
  }
  return eventId;
};

const readCookie = (name) => {
  try {
    if (typeof document === 'undefined' || !document.cookie) return '';
    const escaped = name.replace(/([.*+?^${}()|[\]\\])/g, '\\$1');
    const match = document.cookie.match(new RegExp('(?:^|; )' + escaped + '=([^;]*)'));
    return match ? decodeURIComponent(match[1]) : '';
  } catch (e) {
    return '';
  }
};

// _fbp is set by the pixel itself. _fbc is derived from the fbclid URL param when
// the cookie has not been written yet (Meta format: fb.1.<timestamp>.<fbclid>).
const getFbp = () => readCookie('_fbp');
const getFbc = () => {
  const cookie = readCookie('_fbc');
  if (cookie) return cookie;
  try {
    if (typeof window === 'undefined') return '';
    const fbclid = new URLSearchParams(window.location.search).get('fbclid');
    if (fbclid) return `fb.1.${Date.now()}.${fbclid}`;
  } catch (e) {
    // ignore
  }
  return '';
};

// Fire-and-forget JSON POST that survives an immediate window.location redirect
// (keepalive). Never throws, no-ops during prerender or when fetch is missing.
const postKeepalive = (url, body) => {
  try {
    if (isPrerender() || typeof fetch !== 'function') return;
    fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      keepalive: true,
      credentials: 'omit',
    }).catch(() => { /* never block checkout */ });
  } catch (e) {
    // never block checkout
  }
};

const metaContentData = (planKey, plan) => {
  const data = PLAN_DATA[planKey];
  if (!data) {
    return { content_ids: [plan || planKey], content_type: 'product' };
  }
  return {
    content_ids: [data.id],
    content_name: data.name,
    content_type: 'product',
    value: data.price,
    currency: 'USD',
  };
};

const checkoutContextKey = (clientReferenceId) => `${CHECKOUT_CONTEXT_PREFIX}${clientReferenceId}`;

const writeCheckoutContext = (clientReferenceId, ctx) => {
  try {
    if (typeof localStorage === 'undefined') return;
    localStorage.setItem(checkoutContextKey(clientReferenceId), JSON.stringify(ctx));
  } catch (e) {
    // localStorage may be blocked
  }
};

const readCheckoutContext = (clientReferenceId) => {
  try {
    if (typeof localStorage === 'undefined') return null;
    const raw = localStorage.getItem(checkoutContextKey(clientReferenceId));
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
};

const removeCheckoutContext = (clientReferenceId) => {
  try {
    if (typeof localStorage === 'undefined') return;
    localStorage.removeItem(checkoutContextKey(clientReferenceId));
  } catch (e) {
    // ignore
  }
};

// Sweep checkout-context entries older than the TTL (or missing a timestamp) so
// stale identifiers from abandoned or long-past checkouts never linger or fire a
// phantom conversion on a later success-page visit.
const sweepStaleCheckoutContexts = () => {
  try {
    if (typeof localStorage === 'undefined') return;
    const now = Date.now();
    const stale = [];
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i);
      if (!key || key.indexOf(CHECKOUT_CONTEXT_PREFIX) !== 0) continue;
      let createdAt = 0;
      try {
        const parsed = JSON.parse(localStorage.getItem(key) || '{}');
        createdAt = Number(parsed && parsed.created_at) || 0;
      } catch (e) {
        createdAt = 0;
      }
      if (!createdAt || (now - createdAt) > CHECKOUT_CONTEXT_TTL_MS) {
        stale.push(key);
      }
    }
    stale.forEach((key) => {
      try { localStorage.removeItem(key); } catch (e) { /* ignore */ }
    });
  } catch (e) {
    // ignore
  }
};

// ViewContent - all visitors, on initial load + each SPA route change.
export const trackMetaViewContent = () => safeMetaTrack('ViewContent');

// AddToCart - the price-button click that routes to the start page.
export const trackMetaAddToCart = (planKey) =>
  safeMetaTrack('AddToCart', metaContentData(planKey, planKey));

// ---------------------------------------------------------------------------
// Booking funnel (Calendly).
//
// The main-flow conversion is a booked call, not a Stripe checkout - every main
// CTA points at #book-call. Without these the dataset receives ONLY PageView and
// ViewContent from this site, so Meta has no conversion signal to optimize
// toward. Calendly's own in-iframe pixel emits non-standard events
// (invitee_event_type_page, invitee_select_day, ...) which are not standard
// events and cannot be used as an optimization goal - these two can.
//
//   Lead     - a date + time was selected (mid-funnel intent, higher volume).
//   Schedule - the booking was confirmed (the actual conversion).
//
// Browser-side only. The public /api/meta/track mirror deliberately allows only
// InitiateCheckout (no trusted conversion may be emitted from an unauthenticated
// endpoint), so a server-side CAPI copy of Schedule needs a trusted source (the
// Calendly webhook) and is out of scope here.
// ---------------------------------------------------------------------------
const BOOKING_CONTENT = { content_name: 'book_a_call', content_category: 'booking' };

export const trackMetaBookingIntent = () => safeMetaTrack('Lead', BOOKING_CONTENT);

// Derive a stable eventID for one booking from a Calendly invitee URI, e.g.
// https://api.calendly.com/scheduled_events/AAA/invitees/BBB -> calendly_BBB.
//
// This is the ONE value that keeps the browser Schedule and the server-side CAPI
// Schedule from being counted twice: Meta dedups a pair only when event_name AND
// event_id both match. The two sides read the invitee URI from different places
// (the embed's postMessage here, the invitee.created webhook on the server), so
// reducing both to the invitee's trailing id makes the match immune to host,
// scheme, or trailing-slash differences between those two representations. The
// backend applies the identical rule in _calendly_event_id().
export const calendlyEventId = (inviteeUri) => {
  if (typeof inviteeUri !== 'string') return null;
  const segments = inviteeUri.split('?')[0].split('/').filter(Boolean);
  const last = segments[segments.length - 1];
  return last ? `calendly_${last}` : null;
};

// Pass Calendly's invitee URI. Falls back to a fresh uuid only when no URI is
// available - the event still fires (losing the booking signal is worse than a
// duplicate Meta will not merge), it just cannot dedup against the server copy.
export const trackMetaBookingScheduled = (inviteeUri = null) =>
  safeMetaTrack('Schedule', BOOKING_CONTENT, calendlyEventId(inviteeUri));

// InitiateCheckout - fired on start-page submit for EVERY plan (including the $9
// trial), just before the Payment Link redirect. The trusted conversions
// (Purchase / StartTrial) are NOT fired here pre-redirect - they fire on payment
// success: server-side from the Stripe webhook, and browser-side on the on-domain
// success route. This also persists the checkout context for that success-page
// conversion, reads _fbp/_fbc, and POSTs the identifiers the Stripe webhook cannot
// see to the backend stash so the CAPI copy can match + dedup. Synchronous and
// fail-safe: the stash POST is fire-and-forget with keepalive so it can never
// delay or break the redirect. Returns the eventID used.
export const trackMetaCheckoutStart = ({ planKey, plan, clientReferenceId, email = '', apiUrl = '' }) => {
  const data = PLAN_DATA[planKey];
  const value = data ? data.price : 0;
  const currency = 'USD';
  const customData = metaContentData(planKey, plan);

  // InitiateCheckout for ALL plans, with a fresh uuid eventID.
  const eventId = safeMetaTrack('InitiateCheckout', customData);

  // Sweep stale entries, then persist THIS checkout's context keyed by
  // client_reference_id so the success route can positively match it. A stale or
  // mismatched context can therefore never fire a phantom Purchase / StartTrial.
  sweepStaleCheckoutContexts();
  if (clientReferenceId) {
    writeCheckoutContext(clientReferenceId, {
      client_reference_id: clientReferenceId,
      // session_id is the leading segment of {session_id}__{plan}; the success
      // route resolves the same value and matches on it.
      session_id: clientReferenceId.split('__')[0] || '',
      plan: plan || planKey,
      plan_key: planKey,
      value,
      currency,
      created_at: Date.now(),
    });
  }

  const fbp = getFbp();
  const fbc = getFbc();
  const eventSourceUrl = typeof window !== 'undefined' ? window.location.href : '';

  // Stash _fbp/_fbc + eventID + source URL for the server-side CAPI copy, keyed by
  // client_reference_id (the Stripe webhook reads it back to enrich + dedup the
  // Purchase / StartTrial). keepalive survives the window.location redirect below.
  postKeepalive(`${apiUrl}${CAPI_STASH_PATH}`, {
    client_reference_id: clientReferenceId,
    fbp,
    fbc,
    event_id: eventId,
    event_source_url: eventSourceUrl,
  });

  // Mirror InitiateCheckout server-side for every plan, sharing the eventID so
  // the browser + server pair dedups. InitiateCheckout carries no trusted
  // conversion value, so the public mirror is allowed to emit it.
  postKeepalive(`${apiUrl}${CAPI_TRACK_PATH}`, {
    event_name: 'InitiateCheckout',
    event_id: eventId,
    event_source_url: eventSourceUrl,
    fbp,
    fbc,
    email,
    custom_data: { value, currency, content_ids: customData.content_ids },
  });

  return eventId;
};

// Browser-side conversion on the on-domain success route (/order-success/:slug):
// Purchase for paid plans, StartTrial for the $9 trial. Fires with
// eventID = client_reference_id so it dedups against the server-side CAPI event
// from the Stripe webhook. It fires ONLY on a POSITIVE match: the resolved
// session must have a checkout context stored under its own client_reference_id
// ({session_id}__{plan}). If no matching context is found, it SKIPS and lets the
// server-side webhook conversion be the source of truth - so a stale or
// mismatched context can never fire a phantom conversion. Consumes the matched
// entry so it fires at most once. Never throws. Returns the eventID, or null.
export const trackMetaConversionOnSuccess = ({ sessionId, plan } = {}) => {
  try {
    sweepStaleCheckoutContexts();
    if (!sessionId || !plan) return null;

    const clientReferenceId = `${sessionId}__${plan}`;
    const ctx = readCheckoutContext(clientReferenceId);
    // Positive match: the stored context must exist AND its identifiers must
    // match the resolved session. Anything short of that -> do not fire.
    if (!ctx || ctx.client_reference_id !== clientReferenceId || ctx.session_id !== sessionId) {
      return null;
    }

    const isTrial = ctx.plan_key === 'trial9' || ctx.plan === 'trial9';
    const contentIds = ctx.plan_key ? [ctx.plan_key] : [ctx.plan];
    // StartTrial carries no value (it mirrors the server StartTrial, which sends
    // none); Purchase carries value + currency.
    const customData = isTrial
      ? { content_ids: contentIds, content_name: ctx.plan, content_type: 'product' }
      : { content_ids: contentIds, content_name: ctx.plan, content_type: 'product', value: ctx.value, currency: ctx.currency || 'USD' };

    const eventId = safeMetaTrack(isTrial ? 'StartTrial' : 'Purchase', customData, clientReferenceId);

    // Consume the matched entry so the conversion fires at most once.
    removeCheckoutContext(clientReferenceId);
    return eventId;
  } catch (e) {
    console.warn('Meta success conversion error:', e);
    return null;
  }
};
